import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ShieldCheck, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr, fmtDate } from "../../lib/api";
import { Loading, PageHeader, StatusPill, ConfirmDelete } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

function CmDetail({ cmId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/care-managers/${cmId}/detail`).then((r) => setD(r.data)).catch((e) => { toast.error(formatErr(e)); setD({ error: true }); }); }, [cmId]);
  if (!d) return <Loading label="Loading workload…" />;
  if (d.error) return <p className="mt-3 text-sm text-stone-400">Workload unavailable.</p>;
  return (
    <div className="mt-4 space-y-4 border-t border-stone-100 pt-4" data-testid={`cm-detail-${cmId}`}>
      <div>
        <p className="p1-label">Assigned families ({d.families.length})</p>
        {d.families.length === 0 ? <p className="text-xs text-stone-400">No active family assignments.</p> : (
          <ul className="mt-1 space-y-1.5">
            {d.families.map((f) => (
              <li key={f.id} className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2 text-xs" data-testid={`cm-family-${f.id}`}>
                <span className="font-semibold text-stone-700">{f.name} <span className="font-normal text-stone-400">· {f.city} · {f.status}</span></span>
                <Link to={`/app/admin/customers?id=${f.id}&assign=1`} className="font-bold text-emerald-900" data-testid={`cm-reassign-${f.id}`}>Reassign</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <p className="p1-label">Today's visits ({d.visits_today.length})</p>
        {d.visits_today.length === 0 ? <p className="text-xs text-stone-400">No visits today.</p> : (
          <ul className="mt-1 space-y-1.5">
            {d.visits_today.map((v) => (
              <li key={v.id}>
                <Link to={`/app/admin/visits/${v.id}`} className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2 text-xs hover:bg-emerald-50" data-testid={`cm-visit-${v.id}`}>
                  <span className="font-semibold text-stone-700">{v.parent_name || "Visit"} · {(v.scheduled_at || "").slice(11, 16)}</span>
                  <StatusPill status={v.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <p className="p1-label">Upcoming visits ({d.upcoming_visits.length})</p>
        {d.upcoming_visits.length === 0 ? <p className="text-xs text-stone-400">Nothing scheduled ahead.</p> : (
          <ul className="mt-1 space-y-1.5">
            {d.upcoming_visits.slice(0, 6).map((v) => (
              <li key={v.id}>
                <Link to={`/app/admin/visits/${v.id}`} className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2 text-xs hover:bg-emerald-50" data-testid={`cm-upcoming-${v.id}`}>
                  <span className="font-semibold text-stone-700">{v.parent_name || "Visit"} · {fmtDate(v.scheduled_at)}</span>
                  <StatusPill status={v.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-stone-400">Lifetime completed visits: <strong>{d.completed_visits}</strong></p>
    </div>
  );
}

export default function CareManagers() {
  const [cms, setCms] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", zones: "", languages: "Hindi, English" });
  const { user } = useAuth();
  const canCreate = ["ops_manager", "admin", "super_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const [delTarget, setDelTarget] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const deleteCm = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/admin/staff/${delTarget.user_id}`);
      toast.success(`Care Manager deleted.${r.data.assignments_cleared ? ` ${r.data.assignments_cleared} assignment(s) cleared and families unassigned.` : ""}`);
      setDelTarget(null); load();
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };
  const load = () => api.get("/admin/care-managers").then((r) => setCms(r.data));
  useEffect(() => { load().catch(() => setCms([])); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/care-managers", { ...form, zones: form.zones.split(",").map((s) => s.trim()).filter(Boolean), languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Care Manager created (default password ParentOne@123)"); setShowForm(false); load();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const setVerify = async (id, status) => {
    await api.patch(`/admin/care-managers/${id}`, { verification_status: status });
    toast.success(`Marked ${status}`); load();
  };
  if (!cms) return <Loading />;
  return (
    <div data-testid="admin-care-managers">
      <PageHeader title="Care Managers" sub="Verification, skills, zones, availability and workload."
        actions={canCreate && <button onClick={() => setShowForm(!showForm)} className="p1-btn-primary !px-5 !py-2.5" data-testid="add-cm-btn"><Plus className="h-4 w-4" /> Add Care Manager</button>} />
      {showForm && canCreate && (
        <form onSubmit={submit} className="p1-card mb-6 grid gap-4 p-5 sm:grid-cols-2" data-testid="cm-form">
          <input className="p1-input" placeholder="Full name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cm-name" />
          <input className="p1-input" placeholder="Email *" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cm-email" />
          <input className="p1-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cm-phone" />
          <input className="p1-input" placeholder="Zones (comma separated)" value={form.zones} onChange={(e) => setForm({ ...form, zones: e.target.value })} data-testid="cm-zones" />
          <div className="sm:col-span-2"><button className="p1-btn-primary !px-5 !py-2.5" data-testid="cm-submit">Create</button></div>
        </form>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {cms.map((cm) => (
          <div key={cm.id} className="p1-card p-5" data-testid={`cm-card-${cm.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-lg font-semibold text-stone-900">{cm.name}</p>
                <p className="text-xs text-stone-500">{cm.email} · {cm.phone}</p>
                <p className="mt-1 text-xs text-stone-500">{(cm.languages || []).join(", ")} · {(cm.zones || []).join(", ") || "All Jaipur"}</p>
              </div>
              <StatusPill status={cm.verification_status} />
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-stone-600">
              <span>Families: <strong>{cm.assigned_families}/{cm.max_families}</strong></span>
              <span>Completed visits: <strong>{cm.completed_visits}</strong></span>
              <span>Availability: <strong className="capitalize">{cm.availability}</strong></span>
            </div>
            {cm.training?.length > 0 && <p className="mt-2 text-xs text-stone-400">Training: {cm.training.join(" · ")}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => setExpanded(expanded === cm.id ? null : cm.id)} className="inline-flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600 hover:border-emerald-700" data-testid={`workload-cm-${cm.id}`}>
                {expanded === cm.id ? <><ChevronUp className="h-3.5 w-3.5" /> Hide workload</> : <><ChevronDown className="h-3.5 w-3.5" /> Workload & families</>}
              </button>
              {cm.verification_status !== "verified" && (
                <button onClick={() => setVerify(cm.id, "verified")} className="inline-flex items-center gap-1 rounded-full bg-emerald-900 px-3 py-1.5 text-xs font-bold text-white" data-testid={`verify-cm-${cm.id}`}><ShieldCheck className="h-3.5 w-3.5" /> Mark verified</button>
              )}
              {cm.availability === "available"
                ? <button onClick={async () => { await api.patch(`/admin/care-managers/${cm.id}`, { availability: "off_duty" }); load(); }} className="rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600" data-testid={`offduty-cm-${cm.id}`}>Set off duty</button>
                : <button onClick={async () => { await api.patch(`/admin/care-managers/${cm.id}`, { availability: "available" }); load(); }} className="rounded-full border border-emerald-700 px-3 py-1.5 text-xs font-semibold text-emerald-900" data-testid={`onduty-cm-${cm.id}`}>Set available</button>}
              {isSuper && (
                <button onClick={() => setDelTarget(cm)} className="rounded-full border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50" data-testid={`delete-cm-${cm.id}`}>
                  <Trash2 className="mr-1 inline h-3 w-3" />Delete</button>
              )}
            </div>
            {expanded === cm.id && <CmDetail cmId={cm.id} />}
          </div>
        ))}
      </div>
      {delTarget && (
        <ConfirmDelete
          title={`Delete ${delTarget.name}?`}
          body={`Are you sure you want to permanently delete this account? This action cannot be undone.${delTarget.assigned_families > 0 ? ` Warning: this Care Manager is currently assigned to ${delTarget.assigned_families} ${delTarget.assigned_families === 1 ? "family" : "families"} — those families will be marked unassigned and their upcoming visits cancelled.` : ""}`}
          onConfirm={deleteCm}
          onCancel={() => setDelTarget(null)}
          busy={busy}
          testidPrefix="cm-delete"
        />
      )}
    </div>
  );
}
